import {
  BUCKET_LIST_ID,
  type Ascent,
  type AscentInput,
  type BucketList,
  type CustomPeakInput,
  type ListInput,
  type ListItem,
  type ListItemInput,
  type Peak,
  type Profile,
  type ProfileInput,
} from "../../shared/schemas";
import type {
  AscentDetail,
  ChallengesPage,
  Comments,
  FeedItem,
  FollowRow,
  GroupMember,
  Invite,
  MyGroups,
  NoticesPage,
  Page,
  PersonCard,
  PersonRow,
  PushCategory,
  PushSettings,
  ReportInput,
  SocialAscent,
  SocialMe,
  SocialMeInput,
} from "../../shared/social";
import { MAX_ASCENT_PHOTOS, type AscentPhoto } from "../../shared/photos";

/** A photo picked on this device, shown from IndexedDB until the outbox has uploaded it. */
export type LocalPhoto = { id: string; blobKey: string; w: number; h: number };

export type ClientAscent = Ascent & { pending?: boolean; localGpxKey?: string | null; localPhoto?: LocalPhoto | null };

/**
 * GET /api/profile, plus the picture waiting in the outbox (IndexedDB blob) until it has synced. The
 * other fields may be missing while only the avatar is known (an optimistic update on a cold start).
 */
export type ClientProfile = Partial<Omit<Profile, "userId" | "avatarUrl">> & {
  userId: string | null;
  avatarUrl: string | null;
  localAvatarKey?: string | null;
};

/** Every write the app makes is one of these, queued in the IndexedDB outbox. */
export type Op =
  | { type: "ascent.put"; id: string; data: AscentInput }
  | { type: "ascent.delete"; id: string }
  | { type: "gpx.put"; ascentId: string; blobKey: string }
  | { type: "gpx.delete"; ascentId: string }
  // Not `photo.*`: that prefix is the removed uploads' (isLegacyOp drops them).
  | { type: "ascentPhoto.put"; ascentId: string; photoId: string; blobKey: string; w: number; h: number }
  | { type: "ascentPhoto.delete"; ascentId: string; photoId: string }
  | { type: "list.put"; id: string; data: ListInput }
  | { type: "list.delete"; id: string }
  | { type: "list.reorder"; ids: string[] }
  | { type: "listItem.put"; listId: string; peakId: string; data: ListItemInput }
  | { type: "listItem.delete"; listId: string; peakId: string }
  | { type: "listItems.add"; listId: string; peakIds: string[] }
  | { type: "peak.put"; id: string; data: CustomPeakInput }
  | { type: "pins.set"; peakIds: string[] }
  | { type: "avatar.put"; avatarId: string; blobKey: string }
  | { type: "avatar.delete" }
  | { type: "profile.put"; data: ProfileInput }
  // Social: keyed by the other person's id (never the handle, which can change). `person` carries what
  // the optimistic lists show until the server answers.
  | { type: "social.put"; data: SocialMeInput }
  | { type: "follow.put"; person: PersonRow; approval: boolean }
  | { type: "follow.delete"; userId: string }
  | { type: "follower.approve"; person: PersonRow }
  | { type: "follower.remove"; userId: string }
  | { type: "block.put"; person: PersonRow }
  | { type: "block.delete"; userId: string }
  | { type: "report.put"; id: string; data: ReportInput }
  | { type: "notices.read"; until: string }
  | { type: "pause.put"; week: string }
  | { type: "pause.delete"; week: string }
  // `by`: the viewer as others see them, shown at once among who put a piedra (older queued ops lack it).
  | { type: "piedra.put"; ownerId: string; ascentId: string; by?: PersonRow }
  | { type: "piedra.delete"; ownerId: string; ascentId: string; by?: string }
  | { type: "comment.put"; ownerId: string; ascentId: string; id: string; body: string; author: PersonRow; createdAt: string }
  | { type: "comment.delete"; ownerId: string; ascentId: string; id: string }
  | { type: "challenge.join"; id: string }
  | { type: "challenge.leave"; id: string }
  | { type: "push.prefs"; mute: PushCategory[] }
  // Group ascents (docs/archive/PLAN-GROUPS.md §2). Accepting carries the invitation as it was read, so the copy
  // shows in one's log at once, offline too; `id` is the copy's (or one's ascent of that day that joins).
  | { type: "invite.put"; ascentId: string; person: PersonRow }
  | { type: "invite.delete"; ascentId: string; userId: string }
  | { type: "invite.accept"; invite: Invite; id: string }
  | { type: "invite.decline"; ownerId: string; ascentId: string };

/**
 * Ops that only older app versions queued, for user photo uploads (removed). A device may still
 * hold some in its outbox, pending or parked as failed: they are dropped, never sent.
 */
export type LegacyOp =
  | { type: "photo.put"; ascentId: string; photoId: string; blobKey: string; thumbBlobKey: string }
  | { type: "photo.patch" | "photo.delete"; ascentId: string; photoId: string };

export const isLegacyOp = (op: Op | LegacyOp): op is LegacyOp => op.type.startsWith("photo.");

const nowIso = () => new Date().toISOString();

function ascentFromInput(id: string, d: AscentInput, prev?: ClientAscent): ClientAscent {
  return {
    id,
    date: d.date,
    year: Number(d.date.slice(0, 4)),
    startTime: d.startTime ?? null,
    durationMin: d.durationMin ?? null,
    timeToSummitMin: d.timeToSummitMin ?? null,
    elevationGain: d.elevationGain ?? null,
    distanceKm: d.distanceKm ?? null,
    activity: d.activity,
    sacScale: d.sacScale ?? null,
    alpineGrade: d.alpineGrade ?? null,
    difficultyNotes: d.difficultyNotes || null,
    routeName: d.routeName || null,
    startPoint: d.startPoint || null,
    winter: !!d.winter,
    conditions: d.conditions || null,
    companions: d.companions || null,
    rating: d.rating ?? null,
    notes: d.notes || null,
    externalUrl: d.externalUrl || null,
    private: !!d.private,
    peakIds: [...d.peakIds],
    gpxKey: prev?.gpxKey ?? null,
    localGpxKey: prev?.localGpxKey ?? null,
    photos: prev?.photos ?? [],
    localPhoto: prev?.localPhoto ?? null,
    createdAt: prev?.createdAt ?? nowIso(),
    updatedAt: nowIso(),
    // Stored on sync (the server sets it); a new date counts as logged then.
    countedAt: prev && prev.date === d.date ? (prev.countedAt ?? null) : null,
    // Editing never changes the group it belongs to.
    groupId: prev?.groupId ?? null,
    pending: true,
  };
}

function mapAscent(list: ClientAscent[], id: string, fn: (a: ClientAscent) => ClientAscent): ClientAscent[] {
  return list.map((a) => (a.id === id ? fn(a) : a));
}

/** The photos that stay when one more is added: at the cap the oldest goes, as on the server. */
const roomForOne = (photos: AscentPhoto[] = []) => photos.slice(Math.max(0, photos.length - (MAX_ASCENT_PHOTOS - 1)));

/** The ascent an accepted invitation gives: its public fields, its peaks but custom ones, nothing private. */
export function ascentFromInvite(inv: Invite, id: string): ClientAscent {
  const now = nowIso();
  return {
    ...ascentFromInput(id, { date: inv.date, activity: inv.activity, elevationGain: inv.elevationGain, distanceKm: inv.distanceKm, peakIds: sharedPeakIds(inv) }),
    createdAt: now,
    updatedAt: now,
  };
}

/** What a copy of an invitation's ascent climbs: every peak but custom ones (never shared). */
export const sharedPeakIds = (inv: Pick<Invite, "peaks">) => inv.peaks.flatMap((p) => ("id" in p ? [p.id] : []));

/** Optimistically apply a queued op to the cached ascents (same reducer for cache + refetch overlay). */
export function applyToAscents(list: ClientAscent[], op: Op): ClientAscent[] {
  switch (op.type) {
    case "invite.accept": {
      // One's own ascent of that day joins as it is; otherwise the copy is new.
      if (list.some((a) => a.id === op.id)) return list;
      return [ascentFromInvite(op.invite, op.id), ...list].sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));
    }
    case "ascent.put": {
      const prev = list.find((a) => a.id === op.id);
      const next = ascentFromInput(op.id, op.data, prev);
      const rest = list.filter((a) => a.id !== op.id);
      return [next, ...rest].sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));
    }
    case "ascent.delete":
      return list.filter((a) => a.id !== op.id);
    case "gpx.put":
      return mapAscent(list, op.ascentId, (a) => ({ ...a, localGpxKey: op.blobKey }));
    case "gpx.delete":
      return mapAscent(list, op.ascentId, (a) => ({ ...a, gpxKey: null, localGpxKey: null }));
    case "ascentPhoto.put":
      return mapAscent(list, op.ascentId, (a) => ({
        ...a,
        photos: roomForOne(a.photos),
        localPhoto: { id: op.photoId, blobKey: op.blobKey, w: op.w, h: op.h },
      }));
    case "ascentPhoto.delete":
      return mapAscent(list, op.ascentId, (a) => ({
        ...a,
        photos: (a.photos ?? []).filter((p) => p.id !== op.photoId),
        localPhoto: a.localPhoto?.id === op.photoId ? null : (a.localPhoto ?? null),
      }));
    default:
      return list;
  }
}

const itemDefaults = (listId: string, peakId: string, position: number): ListItem => ({
  listId,
  peakId,
  priority: 2,
  targetSeason: null,
  targetYear: null,
  plannedRoute: null,
  notes: null,
  position,
  addedAt: nowIso(),
  firstAscentDate: null,
});

function mapList(lists: BucketList[], id: string, fn: (l: BucketList) => BucketList) {
  return lists.map((l) => (l.id === id ? fn(l) : l));
}

/** The Bucket list before the server has its row (it is created on the first write): empty, first of all. */
const EMPTY_BUCKET: BucketList = {
  id: BUCKET_LIST_ID,
  name: "Bucket list",
  description: null,
  kind: "custom",
  sourceName: null,
  sourceUrl: null,
  licence: null,
  approximate: false,
  position: -1,
  createdAt: "1970-01-01T00:00:00.000Z",
  updatedAt: "1970-01-01T00:00:00.000Z",
  items: [],
};

export const isBucketList = (l: Pick<BucketList, "id">) => l.id === BUCKET_LIST_ID;

/** `lists` with the Bucket list in it: every account has one, shown empty until its first write. */
export function withBucketList(lists: BucketList[]): BucketList[] {
  return lists.some(isBucketList) ? lists : [EMPTY_BUCKET, ...lists];
}

export function applyToLists(lists: BucketList[], op: Op): BucketList[] {
  // Written to before the server has its row: the list appears with the change.
  if ((op.type === "listItem.put" || op.type === "listItems.add") && op.listId === BUCKET_LIST_ID) lists = withBucketList(lists);
  switch (op.type) {
    case "ascent.put":
    case "invite.accept": {
      // Climbed: its peaks leave the Bucket list (the server does the same).
      const climbed = new Set(op.type === "ascent.put" ? op.data.peakIds : sharedPeakIds(op.invite));
      const bucket = lists.find(isBucketList);
      if (!bucket?.items.some((i) => climbed.has(i.peakId))) return lists;
      return mapList(lists, BUCKET_LIST_ID, (l) => ({ ...l, items: l.items.filter((i) => !climbed.has(i.peakId)) }));
    }
    case "list.put": {
      const prev = lists.find((l) => l.id === op.id);
      if (prev) {
        return mapList(lists, op.id, (l) => ({
          ...l,
          name: op.data.name,
          description: op.data.description ?? null,
          position: op.data.position ?? l.position,
          updatedAt: nowIso(),
        }));
      }
      const list: BucketList = {
        id: op.id,
        name: op.data.name,
        description: op.data.description ?? null,
        kind: "custom",
        sourceName: null,
        sourceUrl: null,
        licence: null,
        approximate: false,
        position: op.data.position ?? Math.max(-1, ...lists.map((l) => l.position)) + 1,
        createdAt: nowIso(),
        updatedAt: nowIso(),
        items: [],
      };
      return [...lists, list];
    }
    case "list.delete":
      return op.id === BUCKET_LIST_ID ? lists : lists.filter((l) => l.id !== op.id);
    case "list.reorder": {
      const pos = new Map(op.ids.map((id, i) => [id, i]));
      return lists
        .map((l) => ({ ...l, position: pos.get(l.id) ?? l.position }))
        .sort((a, b) => a.position - b.position);
    }
    case "listItem.put":
      return mapList(lists, op.listId, (l) => {
        const prev = l.items.find((i) => i.peakId === op.peakId);
        const base = prev ?? itemDefaults(op.listId, op.peakId, Math.max(-1, ...l.items.map((i) => i.position)) + 1);
        const item: ListItem = {
          ...base,
          priority: op.data.priority ?? base.priority,
          targetSeason: op.data.targetSeason ?? null,
          targetYear: op.data.targetYear ?? null,
          plannedRoute: op.data.plannedRoute || null,
          notes: op.data.notes || null,
          position: op.data.position ?? base.position,
        };
        return { ...l, items: prev ? l.items.map((i) => (i.peakId === op.peakId ? item : i)) : [...l.items, item] };
      });
    case "listItem.delete":
      return mapList(lists, op.listId, (l) => ({ ...l, items: l.items.filter((i) => i.peakId !== op.peakId) }));
    case "listItems.add":
      return mapList(lists, op.listId, (l) => {
        const have = new Set(l.items.map((i) => i.peakId));
        let pos = Math.max(-1, ...l.items.map((i) => i.position)) + 1;
        const added = [...new Set(op.peakIds)].filter((p) => !have.has(p)).map((p) => itemDefaults(op.listId, p, pos++));
        return { ...l, items: [...l.items, ...added] };
      });
    default:
      return lists;
  }
}

export function applyToExtraPeaks(peaks: Peak[], op: Op): Peak[] {
  if (op.type !== "peak.put") return peaks;
  const d = op.data;
  const peak: Peak = {
    id: op.id,
    name: d.name,
    altNames: null,
    elevation: d.elevation ?? null,
    prominence: null,
    lat: d.lat,
    lon: d.lon,
    massif: d.massif || null,
    country: d.country || null,
    source: "custom",
    osmType: null,
    osmId: null,
    wikidataId: null,
    wikipediaUrl: null,
    imageUrl: null,
    imageAttribution: null,
    inCatalog: false,
    lastSyncedAt: null,
  };
  return [...peaks.filter((p) => p.id !== op.id), peak];
}

/** Pinned profile stamps: the latest set wins. */
export function applyToPins(pins: string[], op: Op): string[] {
  return op.type === "pins.set" ? [...op.peakIds] : pins;
}

export function applyToProfile(p: ClientProfile, op: Op): ClientProfile {
  if (op.type === "avatar.put") return { ...p, localAvatarKey: op.blobKey };
  if (op.type === "avatar.delete") return { ...p, avatarUrl: null, localAvatarKey: null };
  if (op.type === "profile.put") {
    const d = op.data;
    return { ...p, name: d.name.trim(), birthYear: d.birthYear, sex: d.sex, homeBase: d.homeBase.trim() };
  }
  return p;
}

// ── Social ─────────────────────────────────────────────────────────────────────────────────────────

const SOCIAL_OPS = new Set<Op["type"]>([
  "social.put",
  "follow.put",
  "follow.delete",
  "follower.approve",
  "follower.remove",
  "block.put",
  "block.delete",
  "report.put",
  "notices.read",
  "pause.put",
  "pause.delete",
  "piedra.put",
  "piedra.delete",
  "comment.put",
  "comment.delete",
  "challenge.join",
  "challenge.leave",
  "push.prefs",
  "invite.put",
  "invite.delete",
  "invite.accept",
  "invite.decline",
]);
export const isSocialOp = (op: Op) => SOCIAL_OPS.has(op.type);

export function applyToSocialMe(me: SocialMe, op: Op): SocialMe {
  switch (op.type) {
    case "social.put": {
      const d = op.data;
      return { ...me, handle: d.handle ?? null, bio: d.bio ?? null, social: d.social, leaderboards: d.leaderboards, publishDelay: d.publishDelay, zone: d.zone ?? null, comments: d.comments };
    }
    case "follower.approve":
      return { ...me, requests: Math.max(0, me.requests - 1), followers: me.followers + 1 };
    case "notices.read":
      return { ...me, unreadNotices: 0 };
    case "pause.put":
      return me.pauses.includes(op.week) ? me : { ...me, pauses: [...me.pauses, op.week].sort() };
    case "pause.delete":
      return { ...me, pauses: me.pauses.filter((w) => w !== op.week) };
    default:
      return me;
  }
}

/** A person's card, as the viewer's pending follow, unfollow or block will leave it. */
export type ClientPersonCard = PersonCard & { blocked?: boolean };

export function applyToPerson(card: ClientPersonCard, op: Op): ClientPersonCard {
  switch (op.type) {
    case "follow.put":
      if (op.person.id !== card.id) return card;
      return op.approval ? { ...card, relation: card.relation === "active" ? "active" : "pending" } : { ...card, relation: "active", canSeePassport: true };
    case "follow.delete":
      return op.userId === card.id ? { ...card, relation: "none", canSeePassport: card.self || !card.approval } : card;
    case "block.put":
      return op.person.id === card.id ? { ...card, relation: "none", followsYou: false, canSeePassport: false, blocked: true } : card;
    case "block.delete":
      return op.userId === card.id ? { ...card, blocked: false } : card;
    case "follower.remove":
      return op.userId === card.id ? { ...card, followsYou: false } : card;
    default:
      return card;
  }
}

/** The people the viewer follows or asked to follow. */
export function applyToFollowing(rows: FollowRow[], op: Op): FollowRow[] {
  switch (op.type) {
    case "follow.put":
      return [...rows.filter((r) => r.id !== op.person.id), { ...op.person, status: op.approval ? "pending" : "active" }];
    case "follow.delete":
      return rows.filter((r) => r.id !== op.userId);
    case "block.put":
      return rows.filter((r) => r.id !== op.person.id);
    default:
      return rows;
  }
}

export function applyToFollowers(rows: FollowRow[], op: Op): FollowRow[] {
  switch (op.type) {
    case "follower.approve":
      return rows.some((r) => r.id === op.person.id) ? rows : [...rows, { ...op.person, status: "active" }];
    case "follower.remove":
      return rows.filter((r) => r.id !== op.userId);
    case "block.put":
      return rows.filter((r) => r.id !== op.person.id);
    default:
      return rows;
  }
}

export function applyToRequests(rows: FollowRow[], op: Op): FollowRow[] {
  if (op.type === "follower.approve" || op.type === "block.put") return rows.filter((r) => r.id !== op.person.id);
  if (op.type === "follower.remove") return rows.filter((r) => r.id !== op.userId);
  return rows;
}

export function applyToBlocks(rows: PersonRow[], op: Op): PersonRow[] {
  if (op.type === "block.put") return rows.some((r) => r.id === op.person.id) ? rows : [...rows, { ...op.person, avatarUrl: null }];
  if (op.type === "block.delete") return rows.filter((r) => r.id !== op.userId);
  return rows;
}

export function applyToNotices(page: NoticesPage, op: Op): NoticesPage {
  if (op.type === "notices.read") return { ...page, unread: 0, items: page.items.map((n) => (n.createdAt <= op.until ? { ...n, read: true } : n)) };
  if (op.type === "block.put") return { ...page, items: page.items.filter((n) => n.actor?.id !== op.person.id && n.owner?.id !== op.person.id) };
  // An invitation answered: its notice goes (the server deletes it too).
  if (op.type === "invite.accept" || op.type === "invite.decline") {
    const [owner, ascent] = op.type === "invite.accept" ? [op.invite.owner.id, op.invite.ascentId] : [op.ownerId, op.ascentId];
    return { ...page, items: page.items.filter((n) => !(n.kind === "invite" && n.actor?.id === owner && n.ascentId === ascent)) };
  }
  return page;
}

/** The invitations waiting for the viewer, as answering one (or a block) leaves them. */
export function applyToInvites(list: Invite[], op: Op): Invite[] {
  if (op.type === "invite.accept") return list.filter((i) => !(i.owner.id === op.invite.owner.id && i.ascentId === op.invite.ascentId));
  if (op.type === "invite.decline") return list.filter((i) => !(i.owner.id === op.ownerId && i.ascentId === op.ascentId));
  if (op.type === "block.put") return list.filter((i) => i.owner.id !== op.person.id);
  return list;
}

/**
 * One's own ascents' companions and invitations: inviting and withdrawing, accepting (the inviter and the
 * group they had join the copy), deleting an ascent, a block.
 */
export function applyToGroups(g: MyGroups, op: Op): MyGroups {
  switch (op.type) {
    case "invite.put": {
      const members = g.members[op.ascentId] ?? [];
      const invited = g.invited[op.ascentId] ?? [];
      if (members.some((m) => m.id === op.person.id) || invited.some((p) => p.id === op.person.id)) return g;
      return { ...g, invited: { ...g.invited, [op.ascentId]: [...invited, op.person] } };
    }
    case "invite.delete": {
      const invited = (g.invited[op.ascentId] ?? []).filter((p) => p.id !== op.userId);
      const { [op.ascentId]: _, ...rest } = g.invited;
      return { ...g, invited: invited.length ? { ...rest, [op.ascentId]: invited } : rest };
    }
    case "invite.accept": {
      const members: GroupMember[] = [{ ...op.invite.owner, ascentId: op.invite.ascentId }, ...op.invite.group.filter((m) => m.id !== op.invite.owner.id)];
      const had = g.members[op.id] ?? [];
      return { ...g, members: { ...g.members, [op.id]: [...had, ...members.filter((m) => !had.some((h) => h.id === m.id))] } };
    }
    case "ascent.delete": {
      const { [op.id]: _m, ...members } = g.members;
      const { [op.id]: _i, ...invited } = g.invited;
      return { members, invited };
    }
    case "block.put": {
      const drop = <T extends PersonRow>(rec: Record<string, T[]>) =>
        Object.fromEntries(Object.entries(rec).flatMap(([k, v]) => {
          const left = v.filter((p) => p.id !== op.person.id);
          return left.length ? [[k, left]] : [];
        }));
      return { members: drop(g.members), invited: drop(g.invited) };
    }
    default:
      return g;
  }
}

/** The bell's count: reading Avisos clears it at once (what arrives later shows with the next read). */
export function applyToNoticesCount(c: { unread: number }, op: Op): { unread: number } {
  return op.type === "notices.read" ? { unread: 0 } : c;
}

/** What is muted on the phone, the latest choice winning. */
export function applyToPush(s: PushSettings, op: Op): PushSettings {
  return op.type === "push.prefs" ? { ...s, mute: [...op.mute] } : s;
}

/** Someone's ascent (in Actividad or on their profile) as the viewer's piedras and comments leave it. */
export function applyToSocialAscent<T extends SocialAscent>(a: T, ownerId: string, op: Op): T {
  if (!("ascentId" in op) || !("ownerId" in op) || op.ownerId !== ownerId || op.ascentId !== a.id) return a;
  switch (op.type) {
    case "piedra.put":
      return a.piedra ? a : { ...a, piedra: true, piedras: a.piedras + 1 };
    case "piedra.delete":
      return a.piedra ? { ...a, piedra: false, piedras: Math.max(0, a.piedras - 1) } : a;
    case "comment.put":
      return { ...a, comments: a.comments + 1 };
    case "comment.delete":
      return { ...a, comments: Math.max(0, a.comments - 1) };
  }
  return a;
}

export function applyToFeed(page: Page<FeedItem>, op: Op): Page<FeedItem> {
  if (op.type === "block.put") return { ...page, items: page.items.filter((i) => i.person.id !== op.person.id) };
  if (op.type === "follow.delete") return { ...page, items: page.items.filter((i) => i.person.id !== op.userId) };
  return { ...page, items: page.items.map((i) => applyToSocialAscent(i, i.person.id, op)) };
}

export function applyToPersonAscents(page: Page<SocialAscent>, ownerId: string, op: Op): Page<SocialAscent> {
  return { ...page, items: page.items.map((a) => applyToSocialAscent(a, ownerId, op)) };
}

/** One ascent's page: its counts, and the viewer among who put a piedra (first) or gone from it. */
export function applyToAscentDetail(d: AscentDetail, op: Op): AscentDetail {
  const next = applyToSocialAscent(d, d.person.id, op);
  if (next === d || !("ownerId" in op) || op.ownerId !== d.person.id) return next;
  if (op.type === "piedra.put" && op.by) return { ...next, piedraBy: [op.by, ...next.piedraBy.filter((p) => p.id !== op.by!.id)] };
  if (op.type === "piedra.delete" && op.by) return { ...next, piedraBy: next.piedraBy.filter((p) => p.id !== op.by) };
  return next;
}

/** An ascent's comments and piedras, with the viewer's own still waiting in the outbox. */
export function applyToComments(c: Comments, ownerId: string, ascentId: string, op: Op): Comments {
  if (!("ascentId" in op) || !("ownerId" in op) || op.ownerId !== ownerId || op.ascentId !== ascentId) return c;
  switch (op.type) {
    case "piedra.put":
      return c.piedra ? c : { ...c, piedra: true, piedras: c.piedras + 1 };
    case "piedra.delete":
      return c.piedra ? { ...c, piedra: false, piedras: Math.max(0, c.piedras - 1) } : c;
    case "comment.put":
      return c.items.some((x) => x.id === op.id)
        ? c
        : { ...c, items: [...c.items, { id: op.id, author: op.author, body: op.body, createdAt: op.createdAt, mine: true, canDelete: true }] };
    case "comment.delete":
      return { ...c, items: c.items.filter((x) => x.id !== op.id) };
  }
  return c;
}

/**
 * This month's retos as joining or leaving one leaves them (progress comes with the next read). A reto
 * already made stays: its insignia is kept for good.
 */
export function applyToChallenges(page: ChallengesPage, op: Op): ChallengesPage {
  if (op.type !== "challenge.join" && op.type !== "challenge.leave") return page;
  const join = op.type === "challenge.join";
  return {
    ...page,
    items: page.items.map((c) =>
      c.id !== op.id || c.joined === join || c.completedAt
        ? c
        : { ...c, joined: join, progress: join ? (c.progress ?? 0) : null, completedAt: null, completedOn: null, participants: Math.max(0, c.participants + (join ? 1 : -1)) },
    ),
  };
}

/**
 * Which cached queries an op touches (used to invalidate after sync). Accepting an invitation is "groups":
 * a new ascent in one's log, and the social reads around it.
 */
export function opDomain(op: Op): "ascents" | "lists" | "peaks" | "pins" | "profile" | "social" | "groups" {
  if (op.type === "invite.accept") return "groups";
  if (isSocialOp(op)) return "social";
  if (op.type.startsWith("avatar.") || op.type === "profile.put") return "profile";
  if (op.type.startsWith("list")) return "lists";
  if (op.type === "peak.put") return "peaks";
  if (op.type === "pins.set") return "pins";
  return "ascents";
}
